import { cachePdfBlob, getCachedPdfBlob } from './offlineStore';

interface LoadPdfBlobOptions {
  pdfId: string;
  fileName: string;
  fileUrl: string;
}

export const cachePdfBlobBestEffort = async (
  pdfId: string,
  fileName: string,
  blob: Blob
): Promise<void> => {
  try {
    await cachePdfBlob(pdfId, fileName, blob);
  } catch (cacheError) {
    console.warn('Unable to cache PDF for offline use:', cacheError);
  }
};

/**
 * Download a drawing, falling back to the offline cache when the network is
 * unavailable. Persisting a successful download is deliberately best-effort:
 * iOS can reject Blob writes to IndexedDB even when the PDF download itself is
 * valid, and that must not prevent the drawing from opening.
 */
export const loadPdfBlob = async ({
  pdfId,
  fileName,
  fileUrl,
}: LoadPdfBlobOptions): Promise<Blob> => {
  let networkError: unknown;

  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    await cachePdfBlobBestEffort(pdfId, fileName, blob);
    return blob;
  } catch (error) {
    networkError = error;
  }

  try {
    const cached = await getCachedPdfBlob(pdfId);
    if (cached) return cached.blob;
  } catch (cacheError) {
    console.warn('Unable to read PDF from offline cache:', cacheError);
  }

  throw networkError;
};
